import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { ist_day } from '../shared/ist';
import { fetch_payout_teacher_month, post_payout_paid_outside, post_payout_retry } from '../api';
import {
  PARTIAL_NEEDS_INVOICE_TEXT,
  REASON_MAX_LEN,
  full_amount_text,
  has_error,
  paid_outside_errors,
  payout_error,
  payout_reason_error,
  teacher_month_view
} from './adapters';
import { paid_outside_body, retry_body } from './requests';
import useIdempotentPost from './use_idempotent_post';

/*
 * PROD-7828 W4 — one teacher-month's payouts: the money (payable, paid so far, outstanding), why the next
 * file does or does not pay it, every payout line with the bank's answer, the teacher's reports and what
 * the teacher sees. Two money actions, each confirmed in the dialog: retry a failed line with the same
 * details, and record a payment made outside the bank file (amount at most the outstanding).
 * props: pay_month, teacher_uuid, now(), on_close(), on_changed(text), on_error(e, fallback) (401 only).
 */
const LOAD_FAILED_TEXT = 'Could not load this teacher-month.';
const RETRY_FAILED_TEXT = 'The retry may not have been saved. Reload to check.';
const OUTSIDE_FAILED_TEXT = 'The payment may not have been recorded. Reload to check.';

const RetryForm = ({ item, on_done, on_error, on_cancel }) => {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const { busy, send } = useIdempotentPost(RETRY_FAILED_TEXT);
  const reason_err = payout_reason_error(reason);

  const submit = () => {
    // The route takes no client_request_id: the server refuses a second retry (PAYOUT_ITEM_NOT_FAILED).
    send(retry_body(item.uuid, reason), () =>
      post_payout_retry(retry_body(item.uuid, reason))
    ).then((r) => {
      if (r.ok) {
        on_done(`The ${item.amount} line goes in the next bank file with the same details.`);
        return;
      }
      if (r.err.kind === 'auth') on_error(r.raw, RETRY_FAILED_TEXT);
      setError(r.err.text);
      setConfirming(false);
    });
  };

  return (
    <div className="tp-po-form" role="group" aria-label="Retry with same details">
      <div className="tp-field">
        <label htmlFor="tp-po-retry-reason">Why retry with the same details (recorded)</label>
        <textarea
          id="tp-po-retry-reason"
          className="tp-textarea"
          value={reason}
          maxLength={REASON_MAX_LEN * 2}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="For example: the teacher confirmed the account is open again."
        />
        {touched && reason_err ? (
          <span className="tp-neg" role="alert">
            {reason_err}
          </span>
        ) : null}
      </div>
      {confirming ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          Pay {item.amount} again to {item.account}? It goes in the next bank file.
        </div>
      ) : null}
      {error ? (
        <div className="tp-neg" role="alert">
          {error}
        </div>
      ) : null}
      <div className="tp-modal-ft">
        <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_cancel}>
          Cancel
        </button>
        {confirming ? (
          <button
            type="button"
            className="tp-btn"
            onClick={busy ? undefined : submit}
            aria-disabled={busy ? 'true' : undefined}
          >
            {busy ? 'Saving…' : 'Yes, retry'}
          </button>
        ) : (
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => {
              setTouched(true);
              if (!reason_err) setConfirming(true);
            }}
          >
            Retry with same details…
          </button>
        )}
      </div>
    </div>
  );
};

const PaidOutsideForm = ({ view, today, on_done, on_error, on_cancel }) => {
  const upfront_full = view.outside_full_only;
  const [form, setForm] = useState(() => ({
    amount: upfront_full ? full_amount_text(view.outstanding_raw) : '',
    paid_on: '',
    reference: '',
    reason: ''
  }));
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  // Locked to the whole outstanding: known up front from has_confirmed_invoice, else set by a
  // PAYOUT_PARTIAL_NEEDS_INVOICE refusal (older server, or an invoice withdrawn since the load).
  const [full_only, setFullOnly] = useState(upfront_full);
  const { busy, send } = useIdempotentPost(OUTSIDE_FAILED_TEXT);
  const errors = paid_outside_errors(form, view.outstanding_raw, today, full_only);
  const set = (k) => (e) => {
    setConfirming(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  const field_error = (k) =>
    touched && errors[k] ? (
      <span className="tp-neg" role="alert">
        {errors[k]}
      </span>
    ) : null;

  const submit = () => {
    const signature = paid_outside_body({
      ...form,
      teacher_uuid: view.teacher_uuid,
      pay_month: view.pay_month,
      client_request_id: '',
      additional_payment_uuid: ''
    });
    send(signature, (id) => post_payout_paid_outside({ ...signature, client_request_id: id })).then(
      (r) => {
        if (r.ok) {
          on_done(
            `Recorded ₹${form.amount.trim()} paid outside the bank file for ${view.teacher_name}.`
          );
          return;
        }
        if (r.err.kind === 'auth') on_error(r.raw, OUTSIDE_FAILED_TEXT);
        if (r.err.kind === 'full_only') {
          setFullOnly(true);
          setForm((f) => ({ ...f, amount: full_amount_text(view.outstanding_raw) || f.amount }));
        }
        setError(r.err.text);
        setConfirming(false);
      }
    );
  };

  return (
    <div
      className="tp-po-form"
      role="group"
      aria-label="Record a payment made outside the bank file"
    >
      <div className="tp-hint">
        Only for money already paid another way (for example to a deleted account&apos;s last
        approved bank account). It counts as paid and never goes in a bank file. Still owed:{' '}
        {view.outstanding}.
      </div>
      <div className="tp-field-2">
        <div className="tp-field">
          <label htmlFor="tp-po-out-amount">Amount paid (₹)</label>
          <input
            id="tp-po-out-amount"
            className="tp-input"
            inputMode="decimal"
            value={form.amount}
            onChange={set('amount')}
            readOnly={full_only}
            aria-describedby={full_only ? 'tp-po-out-amount-full' : undefined}
            placeholder="2500.00"
          />
          {full_only ? (
            <span id="tp-po-out-amount-full" className="tp-hint">
              {upfront_full
                ? PARTIAL_NEEDS_INVOICE_TEXT
                : `No confirmed invoice: the full ${view.outstanding} still owed.`}
            </span>
          ) : null}
          {field_error('amount')}
        </div>
        <div className="tp-field">
          <label htmlFor="tp-po-out-date">Paid on</label>
          <input
            id="tp-po-out-date"
            className="tp-input"
            type="date"
            max={today}
            value={form.paid_on}
            onChange={set('paid_on')}
          />
          {field_error('paid_on')}
        </div>
      </div>
      <div className="tp-field">
        <label htmlFor="tp-po-out-ref">Payment reference (UTR or receipt)</label>
        <input
          id="tp-po-out-ref"
          className="tp-input"
          value={form.reference}
          onChange={set('reference')}
          maxLength={64}
        />
        {field_error('reference')}
      </div>
      <div className="tp-field">
        <label htmlFor="tp-po-out-reason">Reason (recorded)</label>
        <textarea
          id="tp-po-out-reason"
          className="tp-textarea"
          value={form.reason}
          maxLength={REASON_MAX_LEN * 2}
          onChange={set('reason')}
        />
        {field_error('reason')}
      </div>
      {confirming ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          Record ₹{form.amount.trim()} paid to {view.teacher_name} on {form.paid_on}, reference{' '}
          {form.reference.trim()}? The teacher sees it as paid separately.
        </div>
      ) : null}
      {error ? (
        <div className="tp-neg" role="alert">
          {error}
        </div>
      ) : null}
      <div className="tp-modal-ft">
        <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_cancel}>
          Cancel
        </button>
        {confirming ? (
          <button
            type="button"
            className="tp-btn"
            onClick={busy ? undefined : submit}
            aria-disabled={busy ? 'true' : undefined}
          >
            {busy ? 'Saving…' : 'Yes, record it'}
          </button>
        ) : (
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => {
              setTouched(true);
              if (!has_error(errors)) setConfirming(true);
            }}
          >
            Record payment…
          </button>
        )}
      </div>
    </div>
  );
};

const item_columns = (on_retry) => [
  { key: 'round_no', label: 'Round' },
  { key: 'amount', label: 'Amount', align: 'right' },
  {
    key: 'state',
    label: 'State',
    render: (r) => (
      <div>
        <StatusPill tone={r.state_view.tone}>{r.state_view.label}</StatusPill>
        {r.why ? <div className="tp-hint">{r.why}</div> : null}
      </div>
    )
  },
  { key: 'sent_on', label: 'Sent on' },
  {
    key: 'bank_reference',
    label: 'Bank reference',
    render: (r) => <span className="tp-mono">{r.bank_reference}</span>
  },
  { key: 'account', label: 'Account' },
  {
    key: 'reference',
    label: 'Our reference',
    render: (r) => (
      <div>
        <span className="tp-mono">{r.reference}</span>
        {r.resolution_note ? <div className="tp-hint">{r.resolution_note}</div> : null}
      </div>
    )
  },
  {
    key: 'action',
    label: 'Action',
    hideInCard: true,
    render: (r) =>
      r.retry_allowed ? (
        <button
          type="button"
          className="tp-link"
          aria-label={`Retry the ${r.round_no} payment with the same details`}
          onClick={() => on_retry(r)}
        >
          Retry with same details
        </button>
      ) : null
  }
];

const TeacherMonthDialog = ({ pay_month, teacher_uuid, now, on_close, on_changed, on_error }) => {
  const [status, setStatus] = useState('loading');
  const [view, setView] = useState(null);
  const [load_error, setLoadError] = useState('');
  const [form, setForm] = useState(null); // {kind:'retry', item} | {kind:'outside'}
  const [notice, setNotice] = useState('');
  const seq = useRef(0);
  const today = ist_day(now());

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setStatus('loading');
    fetch_payout_teacher_month(pay_month, teacher_uuid)
      .then((d) => {
        if (mine !== seq.current) return;
        setView(teacher_month_view(d));
        setStatus('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = payout_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, LOAD_FAILED_TEXT);
        setLoadError(v.text);
        setStatus('error');
      });
  }, [pay_month, teacher_uuid, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const done = (text) => {
    setForm(null);
    setNotice(text);
    on_changed(text);
    load();
  };

  return (
    <TpDialog
      title={view ? `${view.teacher_name} · ${pay_month}` : `Teacher-month ${pay_month}`}
      on_close={on_close}
      guard_text={form ? discard_guard_text('form open') : ''}
    >
      <div className="tp-modal tp-modal-wide tp-po-tm">
        {status === 'loading' ? <CustomLoading /> : null}
        {status === 'error' ? <LoadFailed text={load_error} on_retry={load} /> : null}
        {status === 'ready' && view ? (
          <>
            {notice ? (
              <div className="tp-banner tp-banner-ok" role="status">
                {notice}
              </div>
            ) : null}
            <div className="tp-meta-grid">
              <div className="tp-meta-field">
                <div className="tp-meta-k">Month status</div>
                <div className="tp-meta-v">{view.lifecycle || '—'}</div>
              </div>
              <div className="tp-meta-field">
                <div className="tp-meta-k">Payable</div>
                <div className="tp-meta-v">{view.payable}</div>
              </div>
              <div className="tp-meta-field">
                <div className="tp-meta-k">Paid so far</div>
                <div className="tp-meta-v">{view.paid_so_far}</div>
              </div>
              <div className="tp-meta-field">
                <div className="tp-meta-k">Still owed</div>
                <div className="tp-meta-v tp-strong">{view.outstanding}</div>
              </div>
            </div>
            <div className="tp-ctx">Next bank file: {view.eligibility_text}</div>
            <div className="tp-ctx">
              The teacher sees:{' '}
              <StatusPill tone={view.teacher_state.tone}>{view.teacher_state.label}</StatusPill>{' '}
              {view.teacher_headline}
            </div>

            <h3 className="tp-po-h3">Payments</h3>
            <DataTable
              columns={item_columns((item) => setForm({ kind: 'retry', item }))}
              rows={view.items}
              rowKey={(r) => r.uuid}
              emptyText="No payment lines yet."
              cardTitle={(r) => <b>{`${r.round_no} · ${r.amount}`}</b>}
            />
            {form && form.kind === 'retry' ? (
              <RetryForm
                item={form.item}
                on_done={done}
                on_error={on_error}
                on_cancel={() => setForm(null)}
              />
            ) : null}

            {view.claims.length ? (
              <>
                <h3 className="tp-po-h3">Teacher reports: payment not received</h3>
                <ul className="tp-po-claims">
                  {view.claims.map((c) => (
                    <li key={c.claim_uuid}>
                      <StatusPill tone={c.status_view.tone}>{c.status_view.label}</StatusPill>{' '}
                      {c.amount} sent {c.sent_on}, reported {c.created_at} IST
                      {c.note ? `: "${c.note}"` : ''}
                      {c.resolution_note ? ` Resolved: ${c.resolution_note}` : ''}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {view.can_pay_outside && !form ? (
              <div className="tp-modal-ft">
                <button
                  type="button"
                  className="tp-btn-out"
                  onClick={() => setForm({ kind: 'outside' })}
                >
                  Record a payment made outside the bank file…
                </button>
              </div>
            ) : null}
            {form && form.kind === 'outside' ? (
              <PaidOutsideForm
                view={view}
                today={today}
                on_done={done}
                on_error={on_error}
                on_cancel={() => setForm(null)}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default TeacherMonthDialog;
